import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";

// Super Admin > Object Type (Task 24429).
//
// Maintenance for the MasterObjectType table in the master database.
//
// One-to-one with the MasterObjectType table in ZCRM_Master. Search runs on the server against Name, IdExternal.
//
// Columns are curated: audit stamps, marker columns and inherited clone
// boilerplate are not shown. See ztools/masteradmin/manifest_part1.php.

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// Held as a string rather than inlined into the JSX: some descriptions contain
// angle brackets (AUTHCAP<Id>X) which JSX would try to parse as a tag.
const DESCRIPTION = "Maintenance for the MasterObjectType table in the master database.";

const plainCell = (v) =>
  v === null || v === undefined || v === "" ? (
    <span className="text-muted">—</span>
  ) : String(v).length > 60 ? (
    <span title={String(v)}>{`${String(v).slice(0, 60)}…`}</span>
  ) : (
    String(v)
  );

const flagCell = (v) =>
  Number(v) === 1 ? (
    <Badge color="success">Yes</Badge>
  ) : (
    <Badge color="light" className="text-muted">
      No
    </Badge>
  );

const COLUMNS = [
  { key: "Id", cell: plainCell },
  { key: "Name", cell: plainCell },
  { key: "IdExternal", cell: plainCell },
  { key: "Description", cell: plainCell },
  { key: "Order", cell: plainCell },
];

const FIELDS = [
  { name: "Name", type: "text", required: false, maxLength: 200, options: [], hint: "varchar(200)" },
  { name: "IdExternal", type: "text", required: false, maxLength: 200, options: [], hint: "varchar(200)" },
  { name: "Description", type: "textarea", required: false, maxLength: null, options: [], hint: "longtext" },
  { name: "Order", type: "integer", required: false, maxLength: null, options: [], hint: "int" },
];

const renderInput = (field, value, onChange) => {
  const v = value === null || value === undefined ? "" : value;

  if (field.type === "boolean") {
    return (
      <Input
        type="select"
        value={String(Number(v) === 1 ? 1 : 0)}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="0">No</option>
        <option value="1">Yes</option>
      </Input>
    );
  }

  if (field.type === "enum") {
    return (
      <Input type="select" value={v} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {field.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </Input>
    );
  }

  if (field.type === "textarea") {
    return (
      <Input
        type="textarea"
        rows={4}
        value={v}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <Input
      type={
        field.type === "integer" || field.type === "decimal" ? "number" : "text"
      }
      step={field.type === "decimal" ? "any" : undefined}
      maxLength={field.maxLength || undefined}
      value={v}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const MasterObjectType = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [search, setSearch] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await FetchAPI("/masterobjecttype/search", "POST", [
        {
          Search: search.trim(),
          Page: page,
          PageSize: pageSize,
          FlagIncludeDeleted: includeDeleted ? 1 : 0,
        },
      ]);

      if (Number(res?.status) === 200) {
        setRows(res?.masterobjecttype_list || []);
        setSummary(res?.summary || null);
      } else {
        setRows([]);
        setSummary(null);
        toast.error(res?.message || "Could not load Object Type");
      }
    } catch (e) {
      setRows([]);
      setSummary(null);
      toast.error("Could not load Object Type");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, includeDeleted]);

  // Debounced so typing in the search box fires one request, not one per key.
  useEffect(() => {
    const timer = setTimeout(() => load(), 350);
    return () => clearTimeout(timer);
  }, [load]);

  const setField = (name, value) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  const openEditor = async (row) => {
    if (!row) {
      const blank = {};
      FIELDS.forEach((f) => {
        blank[f.name] = f.type === "boolean" ? "0" : "";
      });
      setEditing(null);
      setForm(blank);
      setEditorOpen(true);
      return;
    }

    // The grid only carries the list columns, so the whole row is fetched
    // rather than opening a form with most of its fields missing.
    try {
      const res = await FetchAPI(`/masterobjecttype/data/${row["Id"]}`, "GET");
      if (Number(res?.status) !== 200) {
        toast.error(res?.message || "Could not open that row");
        return;
      }
      const full = res?.masterobjecttype_data || {};
      const next = {};
      FIELDS.forEach((f) => {
        next[f.name] = full[f.name] === null || full[f.name] === undefined ? "" : full[f.name];
      });
      setEditing(full);
      setForm(next);
      setEditorOpen(true);
    } catch (e) {
      toast.error("Could not open that row");
    }
  };

  const save = async () => {
    const payload = { ...form };
    if (editing) payload["Id"] = editing["Id"];

    setSaving(true);
    try {
      const res = editing
        ? await FetchAPI("/masterobjecttype/update", "PUT", [payload])
        : await FetchAPI("/masterobjecttype/create", "POST", [payload]);

      if (Number(res?.status) === 200) {
        toast.success(editing ? "Saved" : "Created");
        setEditorOpen(false);
        setEditing(null);
        await load();
      } else {
        toast.error(res?.message || "Could not save that row");
      }
    } catch (e) {
      toast.error("Could not save that row");
    }
    setSaving(false);
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await FetchAPI("/masterobjecttype/delete", "DELETE", [
        { "Id": pendingDelete["Id"] },
      ]);
      if (Number(res?.status) === 200) {
        toast.success("Deleted");
        setPendingDelete(null);
        await load();
      } else {
        toast.error(res?.message || "Could not delete that row");
      }
    } catch (e) {
      toast.error("Could not delete that row");
    }
    setDeleting(false);
  };
  const pageQty = summary?.PageQty || 1;

  return (
    <React.Fragment>
      <DeleteModal
        show={!!pendingDelete}
        loading={deleting}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setPendingDelete(null)}
        customText="Deleting stamps DeletedDateTime; the row stays in the table."
      />
      <Card>
        <CardHeader>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <h5 className="card-title mb-1">
                Object Type{" "}
                <span className="text-muted fs-12">(MasterObjectType)</span>
              </h5>
              <p className="text-muted mb-1 fs-13">{DESCRIPTION}</p>
              <div className="d-flex flex-wrap gap-1">
              </div>
            </div>
            {/* create */}
            <Button color="success" onClick={() => openEditor(null)}>
              <i className="ri-add-line align-bottom me-1"></i>
              Add
            </Button>
          </div>

          <Row className="mt-3 g-2 align-items-end">
            <Col sm={6} md={5}>
              <label className="form-label mb-1 fs-13">Search</label>
              <Input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </Col>            <Col sm={6} md={2}>
              <label className="form-label mb-1 fs-13">Per page</label>
              <Input
                type="select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Input>
            </Col>
            <Col sm={6} md={3}>
              <div className="form-check mt-4">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="inc-deleted"
                  checked={includeDeleted}
                  onChange={(e) => {
                    setIncludeDeleted(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label fs-13" htmlFor="inc-deleted">
                  Include deleted
                </label>
              </div>
            </Col>            <Col sm={6} md={2}>
              <Button
                color="light"
                className="w-100"
                onClick={load}
                disabled={loading}
              >
                Refresh
              </Button>
            </Col>
          </Row>

          {summary && !loading && (
            <p className="text-muted mb-0 mt-2 fs-13">
              {Number(summary.RowQty).toLocaleString()} row
              {summary.RowQty === 1 ? "" : "s"} — page {summary.Page} of{" "}
              {pageQty}
            </p>
          )}
        </CardHeader>

        <CardBody className="pt-0">
          {loading ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <NoDataContent
              titleText="Nothing to show"
              paragraphText="No rows matched."
              useButton={false}
            />
          ) : (
            <>
              <div className="table-responsive table-card">
                <Table className="align-middle table-nowrap mb-0">
                  <thead className="text-muted">
                    <tr>
                      {COLUMNS.map((c) => (
                        <th key={c.key}>{c.key}</th>
                      ))}
                      <th style={{ width: 110 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={`${row["Id"] ?? "r"}-${i}`}>
                        {COLUMNS.map((c) => (
                          <td key={c.key}>{c.cell(row[c.key])}</td>
                        ))}
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-soft-info"
                            title="Edit"
                            onClick={() => openEditor(row)}
                          >
                            <i className="ri-pencil-fill"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-soft-danger"
                            title="Delete"
                            onClick={() => setPendingDelete(row)}
                          >
                            <i className="ri-delete-bin-fill"></i>
                          </button>
                        </div>
                      </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {pageQty > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Button
                    color="light"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-muted fs-13">
                    Page {page} of {pageQty}
                  </span>
                  <Button
                    color="light"
                    disabled={page >= pageQty}
                    onClick={() => setPage((p) => Math.min(pageQty, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* editor */}
      <AppModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        size="lg"
        title={editing ? `Edit Object Type #${editing["Id"]}` : "New Object Type"}
        subtitle="MasterObjectType"
        loading={saving}
        loadingText="Saving…"
        onSubmit={save}
        submitLabel="Save"
      >
        <Row className="g-3">
          {FIELDS.map((f) => (
            <Col key={f.name} md={f.type === "textarea" ? 12 : 6}>
              <label className="form-label mb-1 fs-13">
                {f.name}
                {f.required && <span className="text-danger"> *</span>}
              </label>
              {renderInput(f, form[f.name], (v) => setField(f.name, v))}
              <small className="text-muted fs-11">{f.hint}</small>
            </Col>
          ))}
        </Row>
      </AppModal>
    </React.Fragment>
  );
};

export default MasterObjectType;
